import React, { useState, useEffect, useCallback } from 'react';
import { Sun, RefreshCw, Users, User, List, CalendarDays } from 'lucide-react';
import TasksSection       from './myDay/TasksSection';
import HotLeadsSection    from './myDay/HotLeadsSection';
import AtRiskLeadsSection from './myDay/AtRiskLeadsSection';
import AppointmentsSection from './myDay/AppointmentsSection';
import FollowupsSection   from './myDay/FollowupsSection';
import MentionsSection    from './myDay/MentionsSection';
import UnrespondedLeadAlert from '../../components/UnrespondedLeadAlert';
import CalendarView from './myDay/CalendarView';
import ContactDrawer from '../../components/ContactDrawer';

/* ═══════════════════════════════════════════════════════════════════════════
   MyDayTab — personal queue + team-visibility landing page.

   Uses the crm:my-day IPC channel (Phase A, commit 2cabf24).
   Response shape: { scope, mine, team, counts: { mine, team } }

   The Mine/Team toggle re-calls with scope='team'. Mine always fetches
   first on mount; team fetch is lazy (only when toggled).
   ═══════════════════════════════════════════════════════════════════════════ */

const EMPTY_MINE = { tasks: [], hotLeads: [], atRiskLeads: [], appointments: [], followups: [], mentions: [] };
const EMPTY_COUNTS = { tasks: 0, hotLeads: 0, atRiskLeads: 0, appointments: 0, followups: 0, mentions: 0 };

export default function MyDayTab({ user, onNavigate, onAppNavigate, onOpenContact }) {
  // PR-A: every Pending-box row (queue sections, calendar day-panel, critical
  // alert bar) opens the contact slide-over DRAWER — review without leaving the
  // list. The drawer's "Full profile" link falls back to the existing full-tab
  // open (onOpenContact). Contacts with no linked id do nothing.
  const [drawerContactId, setDrawerContactId] = useState(null);
  const openContact = (id) => {
    if (id) setDrawerContactId(id);
  };

  // Queue | Calendar view toggle. Default 'queue' so everyone lands on the
  // existing personal queue. The calendar render tree is only mounted when its
  // tab is active (#129 — the queue view carries zero added DOM/cost when the
  // calendar is not shown). Last-selected view is persisted per-user.
  const viewKey = `myDayView:${user?.id || 'anon'}`;
  const [topView, setTopView] = useState(() => {
    try { return localStorage.getItem(viewKey) === 'calendar' ? 'calendar' : 'queue'; }
    catch { return 'queue'; }
  });
  function selectTopView(next) {
    if (next === topView) return;
    setTopView(next);
    try { localStorage.setItem(viewKey, next); } catch { /* private mode */ }
  }

  const [scope, setScope] = useState('mine');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (requestedScope) => {
    setLoading(true);
    setError(null);
    try {
      const result = await window.zignagenixAPI.getMyDay(requestedScope);
      setData(result);
    } catch (e) {
      setError(e?.message || 'Failed to load your day. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch the queue when the queue view is actually shown — the calendar
  // view has its own per-user fetches and must not pay for the queue payload.
  useEffect(() => { if (topView === 'queue') load(scope); }, [scope, load, topView]);

  function handleScopeChange(next) {
    if (next === scope) return;
    setScope(next);
  }

  function refresh() { load(scope); }

  const isTeam = scope === 'team';
  // Normalize: every section expects an array. If the IPC response ever
  // returns a malformed shape (e.g. {error: ...} on a failure, or null
  // for a missing scope), fall back to empty arrays per-section so the
  // child sections' .map() / .filter() calls don't crash.
  function _safeSection(raw) {
    const src = raw && typeof raw === 'object' ? raw : {};
    return {
      tasks:        Array.isArray(src.tasks)        ? src.tasks        : [],
      hotLeads:     Array.isArray(src.hotLeads)     ? src.hotLeads     : [],
      atRiskLeads:  Array.isArray(src.atRiskLeads)  ? src.atRiskLeads  : [],
      appointments: Array.isArray(src.appointments) ? src.appointments : [],
      followups:    Array.isArray(src.followups)    ? src.followups    : [],
      mentions:     Array.isArray(src.mentions)     ? src.mentions     : [],
    };
  }
  const sectionData = _safeSection(isTeam ? data?.team : data?.mine);
  const mineCounts = data?.counts?.mine || EMPTY_COUNTS;
  const teamCounts = data?.counts?.team || EMPTY_COUNTS;
  const activeCounts = isTeam ? teamCounts : mineCounts;

  return (
    <div style={{ padding: '4px 0' }}>
      {drawerContactId != null && (
        <ContactDrawer
          contactId={drawerContactId}
          user={user}
          onClose={() => setDrawerContactId(null)}
          onFullProfile={(id) => { setDrawerContactId(null); if (onOpenContact) onOpenContact(id); }}
        />
      )}

      {/* ── Unresponded-lead urgent alert (Ryland) ──────────────────
          Team-wide + leadership-visible; blinks + chimes until the lead
          is responded to or moved. Always mounted at the top of My Day,
          independent of the mine/team toggle. */}
      <UnrespondedLeadAlert onOpenContact={openContact} />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Sun size={20} color="#F26522" />
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#212529' }}>My Day</h3>
            <p style={{ margin: 0, fontSize: 11, color: '#6C757D' }}>Your personal queue for {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Company view — O-2 entry point for calendar:oversight holders (e.g. the
              CEO reaches Executive Calendar Oversight straight from his own My Day).
              Permission-gated; invisible to staff without calendar:oversight. */}
          {(user?.permissions || []).includes('calendar:oversight') && onAppNavigate && (
            <button
              onClick={() => onAppNavigate('calendarOversight')}
              aria-label="Open company calendar oversight"
              style={{ padding: '5px 10px', fontSize: 12, fontWeight: 600, background: '#2E8B98', color: '#fff', border: '1px solid #2E8B98', borderRadius: 6, cursor: 'pointer' }}
            >
              Company view
            </button>
          )}
          {/* Queue / Calendar view toggle */}
          <div className="my-day-scope-toggle" role="group" aria-label="My Day view">
            <button
              className={topView === 'queue' ? 'active' : ''}
              onClick={() => selectTopView('queue')}
              aria-pressed={topView === 'queue'}
            >
              <List size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Queue
            </button>
            <button
              className={topView === 'calendar' ? 'active' : ''}
              onClick={() => selectTopView('calendar')}
              aria-pressed={topView === 'calendar'}
            >
              <CalendarDays size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Calendar
            </button>
          </div>

          {/* Mine / Team toggle — queue view only */}
          {topView === 'queue' && (
          <div className="my-day-scope-toggle" role="group" aria-label="View scope">
            <button
              className={!isTeam ? 'active' : ''}
              onClick={() => handleScopeChange('mine')}
              aria-pressed={!isTeam}
            >
              <User size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Mine
              {mineCounts.tasks + mineCounts.hotLeads + mineCounts.atRiskLeads + mineCounts.followups > 0 && (
                <span className="my-day-count-badge" style={{ marginLeft: 5, background: !isTeam ? '#2E8B98' : '#E9ECEF', color: !isTeam ? '#fff' : '#6C757D' }}>
                  {mineCounts.tasks + mineCounts.hotLeads + mineCounts.atRiskLeads + mineCounts.followups}
                </span>
              )}
            </button>
            <button
              className={isTeam ? 'active' : ''}
              onClick={() => handleScopeChange('team')}
              aria-pressed={isTeam}
            >
              <Users size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Team
              {teamCounts.tasks + teamCounts.hotLeads + teamCounts.atRiskLeads + teamCounts.followups > 0 && (
                <span className="my-day-count-badge" style={{ marginLeft: 5, background: isTeam ? '#2E8B98' : '#E9ECEF', color: isTeam ? '#fff' : '#6C757D' }}>
                  {teamCounts.tasks + teamCounts.hotLeads + teamCounts.atRiskLeads + teamCounts.followups}
                </span>
              )}
            </button>
          </div>
          )}

          {topView === 'queue' && (
          <button
            onClick={refresh}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#F8F9FA', border: 'none', borderRadius: 8, fontSize: 12, color: '#6C757D', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '2px 2px 5px rgba(186,199,216,0.5), -2px -2px 5px rgba(255,255,255,0.9)' }}
            aria-label="Refresh"
          >
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
          )}
        </div>
      </div>

      {/* ── Calendar view (render-gated; mounted only when active) ───── */}
      {topView === 'calendar' && (
        <CalendarView user={user} onOpenContact={openContact} onAppNavigate={onAppNavigate} />
      )}

      {/* ── Queue view ─────────────────────────────────────────────── */}
      {topView === 'queue' && (
      <>
      {/* ── Error ──────────────────────────────────────────────────── */}
      {error && (
        <div style={{ background: '#F8D7DA', color: '#721C24', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Loading skeleton ───────────────────────────────────────── */}
      {loading && !data && (
        <div className="my-day-grid" aria-busy="true" aria-label="Loading">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="my-day-card">
              <div className="my-day-skeleton" style={{ width: '60%', marginBottom: 12 }} />
              <div className="my-day-skeleton" style={{ width: '100%' }} />
              <div className="my-day-skeleton" style={{ width: '85%' }} />
              <div className="my-day-skeleton" style={{ width: '70%' }} />
            </div>
          ))}
        </div>
      )}

      {/* ── Section grid ───────────────────────────────────────────── */}
      {!loading || data ? (
        <div className="my-day-grid">
          <TasksSection
            rows={sectionData.tasks}
            count={activeCounts.tasks}
            isTeam={isTeam}
            onSeeAll={() => onNavigate('tasks')}
            onRowClick={(t) => openContact(t.contact_id)}
          />
          <HotLeadsSection
            rows={sectionData.hotLeads}
            count={activeCounts.hotLeads}
            isTeam={isTeam}
            onSeeAll={() => onNavigate('contacts')}
            onRowClick={(c) => openContact(c.id)}
          />
          <AtRiskLeadsSection
            rows={sectionData.atRiskLeads}
            count={activeCounts.atRiskLeads}
            isTeam={isTeam}
            onSeeAll={() => onNavigate('contacts')}
            onRowClick={(c) => openContact(c.id)}
          />
          <AppointmentsSection
            rows={sectionData.appointments}
            count={activeCounts.appointments}
            isTeam={isTeam}
            onSeeAll={() => onNavigate('calendar')}
            onRowClick={(a) => openContact(a.contact_id)}
          />
          <FollowupsSection
            rows={sectionData.followups}
            count={activeCounts.followups}
            isTeam={isTeam}
            onSeeAll={() => onNavigate('contacts')}
            onRowClick={(c) => openContact(c.id)}
          />
          {/* Mentions only shown in Mine view — team mentions are N/A by design */}
          {!isTeam && (
            <MentionsSection
              rows={(data?.mine || EMPTY_MINE).mentions}
              count={mineCounts.mentions}
              onSeeAll={() => onNavigate('interactions')}
              onRowClick={(m) => openContact(m.contact_id)}
            />
          )}
        </div>
      ) : null}
      </>
      )}
    </div>
  );
}
